import { FC, useEffect, useRef, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
} from "antd"
import { Contract, CutMode, SelectionPolicy, UpdateContractBody } from "../store/types"
import { seedForm } from "../utils"
import { ContractEntitlements } from "./contract-entitlements"
import { GameplayCutSettings } from "./gameplay-cut-settings"

const CUT_MODE_OPTIONS: Array<{ value: CutMode; label: string }> = [
  { value: "full", label: "full — deliver whole recordings" },
  { value: "matches", label: "matches — trim to match boundaries" },
  { value: "multiplayer", label: "multiplayer — trim to co-play windows" },
]

// Fields the planner reads. Kept as a flat form: they are negotiated numbers,
// not a nested object the operator thinks in.
interface PolicyFormValues {
  targetHours?: number
  perGameCapHours?: number
  perGameRaisedCapHours?: number
  raisedCap3pPlusPct?: number
  perGameMinHours?: number
  perGameBatchCapHours?: number
  minSessionDurationMinutes?: number
  maxSessionDurationMinutes?: number
  minPlayers?: number
  maxPlayers?: number
  minConfidence?: number
  effectiveFrom?: string
  trustedCountryCodes?: string[]
  tiers?: Array<{ name: string; gameIds: number[] }>
}

function policyToForm(policy: SelectionPolicy | null): PolicyFormValues {
  return {
    targetHours: policy?.targetHours,
    perGameCapHours: policy?.perGameCapHours,
    perGameRaisedCapHours: policy?.perGameRaisedCapHours,
    raisedCap3pPlusPct: policy?.raisedCap3pPlusPct,
    perGameMinHours: policy?.perGameMinHours,
    perGameBatchCapHours: policy?.perGameBatchCapHours,
    // Minutes in the UI, ms on the wire — nobody negotiates in milliseconds.
    // `!= null` rather than truthiness: 0 is a legitimate stored value meaning
    // "no minimum", and a truthy test would render it blank and then overwrite
    // it with the service default on save.
    minSessionDurationMinutes: policy?.minSessionDurationMs != null ? policy.minSessionDurationMs / 60000 : undefined,
    maxSessionDurationMinutes: policy?.maxSessionDurationMs != null ? policy.maxSessionDurationMs / 60000 : undefined,
    minPlayers: policy?.minPlayers,
    maxPlayers: policy?.maxPlayers,
    minConfidence: policy?.minConfidence,
    effectiveFrom: policy?.effectiveFrom,
    trustedCountryCodes: policy?.trustedCountryCodes,
    tiers: policy?.tiers,
  }
}

function formToPolicy(v: PolicyFormValues): SelectionPolicy {
  const policy: SelectionPolicy = {
    targetHours: v.targetHours,
    perGameCapHours: v.perGameCapHours,
    perGameRaisedCapHours: v.perGameRaisedCapHours,
    raisedCap3pPlusPct: v.raisedCap3pPlusPct,
    perGameMinHours: v.perGameMinHours,
    perGameBatchCapHours: v.perGameBatchCapHours,
    minSessionDurationMs: v.minSessionDurationMinutes != null ? v.minSessionDurationMinutes * 60000 : undefined,
    maxSessionDurationMs: v.maxSessionDurationMinutes != null ? v.maxSessionDurationMinutes * 60000 : undefined,
    minPlayers: v.minPlayers,
    maxPlayers: v.maxPlayers,
    minConfidence: v.minConfidence,
    effectiveFrom: v.effectiveFrom || undefined,
    trustedCountryCodes: v.trustedCountryCodes?.length ? v.trustedCountryCodes : undefined,
    tiers: v.tiers?.length ? v.tiers : undefined,
  }

  // Drop unset keys so a blank field falls back to the ledger default instead of
  // writing an explicit undefined into the stored policy.
  return Object.fromEntries(Object.entries(policy).filter(([, val]) => val !== undefined)) as SelectionPolicy
}

function contractToForm(contract: Contract) {
  return {
    status: contract.status,
    watermark: contract.watermark,
    cutMode: contract.cutConfig?.cutMode ?? "full",
    // Distinct from the selection policy's minPlayers below — same concept
    // name, different setting, and sharing one form key made each overwrite
    // the other in setFieldsValue and left onSave ambiguous.
    cutMinPlayers: contract.cutConfig?.minPlayers ?? undefined,
    pipelineVersion: contract.cutConfig?.pipelineVersion ?? undefined,
    storageType: contract.storageConfig?.type ?? "S3",
    storageBucket: contract.storageConfig?.bucket ?? undefined,
    bucketOwnerFullControl: contract.storageConfig?.bucketOwnerFullControl ?? false,
    includeSessionManifest: contract.storageConfig?.includeSessionManifest ?? false,
    excludeChatScore: contract.storageConfig?.excludeChatScore ?? false,
    ...policyToForm(contract.selectionPolicy),
  }
}

export const ContractSettings: FC<{ contract: Contract }> = observer(({ contract }) => {
  const { dataStore: { dataDeliveryStore: store } } = useStore()
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)
  const [checking, setChecking] = useState(false)

  const seededFor = useRef<Pick<Contract, "id" | "updatedAt"> | null>(null)

  useEffect(() => {
    const seeded = seededFor.current
    if (seeded?.id === contract.id && (seeded.updatedAt === contract.updatedAt || form.isFieldsTouched())) return
    seededFor.current = { id: contract.id, updatedAt: contract.updatedAt }
    seedForm(form, contractToForm(contract))
  }, [form, contract])

  const cutMode: CutMode = Form.useWatch("cutMode", form) ?? "full"
  const storageType: "S3" | "GCS" = Form.useWatch("storageType", form) ?? "S3"

  const onSave = async() => {
    let v
    try {
      v = await form.validateFields()
    } catch {
      return
    }
    const body: UpdateContractBody = {
      status: v.status,
      watermark: v.watermark,
      cutConfig: {
        cutMode: v.cutMode,
        // Explicit null, not undefined: the PATCH only writes keys that are
        // PRESENT, and JSON.stringify drops undefined entirely — so switching
        // away from multiplayer would leave the old minPlayers stored and a
        // later switch back would silently reuse a stale bound.
        minPlayers: v.cutMode === "multiplayer" ? (v.cutMinPlayers ?? null) : null,
        pipelineVersion: v.pipelineVersion ?? null,
      },
      storageConfig: {
        type: v.storageType,
        // Explicit null, like cutConfig above: the PATCH merges per key, so
        // undefined would keep a customer-owned bucket the operator just cleared.
        bucket: v.storageBucket || null,
        bucketOwnerFullControl: v.storageType === "S3" ? v.bucketOwnerFullControl : null,
        includeSessionManifest: v.includeSessionManifest,
        excludeChatScore: v.excludeChatScore,
      },
      selectionPolicy: formToPolicy(v),
    }
    setSaving(true)
    const updated = await store.updateContract(contract.id, body)
    setSaving(false)
    if (updated) {
      seedForm(form, contractToForm(updated))
    }
  }

  // Read-only by default. The write probe puts a foreign object into the
  // customer's bucket, so it is a separate, deliberate action.
  const onCheck = async(writeProbe: boolean) => {
    setChecking(true)
    await store.checkDestination(contract.id, writeProbe)
    setChecking(false)
  }

  const dest = store.destination?.contractId === contract.id ? store.destination : null

  return (
    <Space direction="vertical" style={{ width: "100%" }} size={16}>
      <Card
        size="small"
        title="Delivery destination"
        extra={
          <Space>
            <Button size="small" loading={checking} onClick={() => onCheck(false)}>Check reachable</Button>
            <Button size="small" loading={checking} onClick={() => onCheck(true)}>Test write</Button>
          </Space>
        }
      >
        <Descriptions column={3} size="small">
          <Descriptions.Item label="Derived bucket">
            <span style={{ fontFamily: "monospace", fontSize: 12 }}>{contract.deliveryBucket ?? "—"}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Customer key">{contract.customerKey}</Descriptions.Item>
          <Descriptions.Item label="Preflight">
            {!dest && <Tag>not checked</Tag>}
            {dest && !dest.reachable && <Tag color="red">NOT REACHABLE</Tag>}
            {dest && dest.reachable && dest.writable === true && <Tag color="green">REACHABLE + WRITABLE</Tag>}
            {dest && dest.reachable && dest.writable === false && <Tag color="gold">REACHABLE, NOT WRITABLE</Tag>}
            {dest && dest.reachable && dest.writable === null && <Tag color="cyan">REACHABLE (write untested)</Tag>}
          </Descriptions.Item>
        </Descriptions>
        {dest && (!dest.reachable || dest.writable === false) && (
          <Alert
            type={dest.reachable ? "warning" : "error"}
            showIcon
            style={{ marginTop: 8 }}
            message={`${dest.provider} bucket ${dest.bucket}`}
            description={
              <Space direction="vertical" size={2}>
                {dest.error && <span>{dest.error}</span>}
                {dest.remediation && <span>{dest.remediation}</span>}
              </Space>
            }
          />
        )}
        {dest?.reachable && dest.writable === true && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Bucket accepted a probe write under <code>delivery/{contract.id}/</code> and the probe was removed.
          </Typography.Text>
        )}
        {dest?.reachable && dest.writable === null && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Bucket exists and lists. Only <b>Test write</b> proves the PutObject grant the build needs — it writes and
            removes one probe object in the customer&apos;s bucket.
          </Typography.Text>
        )}
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          Buckets are provisioned by terraform (<code>cust-s3</code>), not from here — the preflight only reports whether the
          destination this contract points at exists and accepts our writes.
        </Typography.Paragraph>
      </Card>

      <ContractEntitlements contract={contract} />

      <Card
        size="small"
        title="Contract configuration"
        extra={<Button type="primary" size="small" loading={saving} onClick={onSave}>Save</Button>}
      >
        <Form form={form} layout="vertical" size="small">
          <Row gutter={[16, 0]}>
            <Col span={6}>
              <Form.Item name="status" label="Status">
                <Select
                  options={["DRAFT", "ACTIVE", "SUSPENDED", "CLOSED"].map(v => ({ value: v, label: v }))}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="watermark" label="Watermark videos" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="cutMode"
                label="Cut mode"
                extra={contract.gameplayCut ? "Overridden by the gameplay cut below." : undefined}
              >
                <Select options={CUT_MODE_OPTIONS} disabled={!!contract.gameplayCut} />
              </Form.Item>
            </Col>
            {cutMode === "multiplayer" && (
              <Col span={6}>
                <Form.Item name="cutMinPlayers" label="Min players to cut" rules={[{ required: true }]}>
                  <InputNumber min={2} style={{ width: "100%" }} disabled={!!contract.gameplayCut} />
                </Form.Item>
              </Col>
            )}
            <Col span={6}>
              <Form.Item
                name="pipelineVersion"
                label="Pipeline version"
                extra="Blank reconciles across ALL versions, which mixes rosters."
              >
                <InputNumber min={1} style={{ width: "100%" }} placeholder="all" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ fontWeight: 600, margin: "8px 0 4px" }}>Storage</div>
          <Row gutter={[16, 0]}>
            <Col span={5}>
              <Form.Item name="storageType" label="Destination">
                <Select options={[{ value: "S3", label: "S3" }, { value: "GCS", label: "GCS" }]} />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item
                name="storageBucket"
                label="Bucket"
                extra={storageType === "GCS" ? "Required for GCS." : "Blank derives from the customer key."}
                rules={storageType === "GCS" ? [{ required: true, message: "GCS needs an explicit bucket" }] : []}
              >
                <Input placeholder={storageType === "GCS" ? "buff-delivery-1" : "derived"} />
              </Form.Item>
            </Col>
            {storageType === "S3" && (
              <Col span={5}>
                <Form.Item
                  name="bucketOwnerFullControl"
                  label="Owner-full-control ACL"
                  valuePropName="checked"
                  extra="Cross-account buckets with ACLs on."
                >
                  <Switch />
                </Form.Item>
              </Col>
            )}
            <Col span={5}>
              <Form.Item
                name="includeSessionManifest"
                label="Write sessions.json"
                valuePropName="checked"
                extra="Co-play grouping without cutting."
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="excludeChatScore" label="Omit chatScore" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ fontWeight: 600, margin: "8px 0 4px" }}>Co-play selection terms</div>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
            The planner selects against these. Blank falls back to the service default; a plan run can override any of them
            for that run only.
          </Typography.Paragraph>
          <Row gutter={[16, 0]}>
            <Col span={4}>
              <Form.Item name="targetHours" label="Target hours / batch">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="perGameCapHours" label="Per-game cap (h)">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="perGameRaisedCapHours" label="Raised cap (h)">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="raisedCap3pPlusPct" label="Raised at 3p+ %">
                <InputNumber min={0} max={100} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="perGameMinHours" label="Per-game min (h)">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="perGameBatchCapHours" label="Per-game / batch (h)">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="minSessionDurationMinutes" label="Min session (min)">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="maxSessionDurationMinutes"
                label="Max session (min)"
                extra="Flagged at validation, not selection."
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="minPlayers" label="Min players">
                <InputNumber min={2} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="maxPlayers" label="Max players" extra="Above this is a lobby.">
                <InputNumber min={2} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="minConfidence" label="Min confidence">
                <InputNumber min={0} max={1} step={0.05} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="effectiveFrom" label="Effective from (ISO)">
                <Input placeholder="2026-07-10T00:00:00Z" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="trustedCountryCodes"
                label="Trusted countries"
                extra="Bypass the missing-pointers gamepad filter."
              >
                <Select mode="tags" tokenSeparators={[","]} style={{ width: "100%" }} placeholder="VN, BI…" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ fontWeight: 600, margin: "8px 0 4px" }}>Tiers</div>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
            Declared most-valuable-first. The planner drains the cheapest tier first so the scarce ones still have room when
            it reaches them.
          </Typography.Paragraph>
          <Form.List name="tiers">
            {(fields, { add, remove }) => (
              <Space direction="vertical" style={{ width: "100%" }} size={4}>
                {fields.map(field => (
                  <Row key={field.key} gutter={8} align="middle">
                    <Col span={5}>
                      <Form.Item
                        name={[field.name, "name"]}
                        rules={[{ required: true, message: "name" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="High" />
                      </Form.Item>
                    </Col>
                    <Col span={16}>
                      <Form.Item name={[field.name, "gameIds"]} style={{ marginBottom: 0 }}>
                        <Select
                          mode="multiple"
                          showSearch
                          optionFilterProp="label"
                          style={{ width: "100%" }}
                          placeholder="games in this tier"
                          options={(store.games ?? []).map(g => ({ value: g.gameId, label: `${g.gameId} · ${g.gameName}` }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={3}>
                      <Button size="small" danger type="text" onClick={() => remove(field.name)}>Remove</Button>
                    </Col>
                  </Row>
                ))}
                <Button size="small" onClick={() => add({ name: "", gameIds: [] })}>Add tier</Button>
              </Space>
            )}
          </Form.List>
        </Form>
      </Card>

      <GameplayCutSettings contract={contract} />
    </Space>
  )
})
